import React from 'react';
import { classNames } from '~/utils/classNames';

export type EnhancerState = 'disabled' | 'default' | 'generating' | 'success';

interface PromptEnhancerButtonProps {
  isMobile?: boolean;
  disabled?: boolean;
  state: EnhancerState;
  onEnhance: () => void;
  onRedo: () => void;
  onApply: () => void;
  onReject: () => void;
  chatStarted: boolean;
}

export const PromptEnhancerButton: React.FC<PromptEnhancerButtonProps> = ({
  isMobile = false,
  disabled = false,
  state,
  onEnhance,
  onRedo,
  onApply,
  onReject,
  chatStarted,
}) => {
  // Disabled State
  if (state === 'disabled') {
    return (
      <button
        disabled
        className={classNames(
          'flex items-center justify-center rounded-lg transition-all',
          'bg-dualite-background-button opacity-50 cursor-not-allowed',
          isMobile ? 'w-8 h-8' : 'h-8 gap-2 px-3',
        )}
        title="Enter a prompt to enhance"
      >
        <div className="i-ph:sparkle text-lg text-dualite-text-primary" />
        {!isMobile && !chatStarted && <span className="text-sm text-dualite-text-primary">Enhance</span>}
      </button>
    );
  }

  // Default State
  if (state === 'default') {
    return (
      <button
        onClick={onEnhance}
        className={classNames(
          'flex items-center justify-center rounded-lg transition-all',
          'bg-dualite-background-button hover:bg-dualite-background-button-hover',
          isMobile ? 'w-8 h-8' : 'h-8 gap-2 px-3',
        )}
      >
        <div className="i-ph:sparkle text-lg text-dualite-text-primary" />
        {!isMobile && !chatStarted && <span className="text-sm text-dualite-text-primary">Enhance</span>}
      </button>
    );
  }

  // Generating/Loading State
  if (state === 'generating') {
    return (
      <button
        disabled
        className={classNames(
          'flex items-center justify-center rounded-lg transition-all',
          'bg-dualite-background-button',
          'cursor-not-allowed w-full h-8 gap-2',
        )}
        title="Generating..."
      >
        <div className="i-svg-spinners:90-ring text-lg text-dualite-text-primary" />
        <span className="text-sm text-dualite-text-primary">Enhancing your prompt</span>
      </button>
    );
  }

  // Success State - shows enhanced result with actions
  return (
    <div className="flex items-center justify-between gap-2 w-full">
      {/* Left side: Blue indicator and redo button */}
      <div className="flex items-center gap-2">
        {/* Enhanced indicator (not a button) */}
        <div
          className={classNames(
            'flex items-center justify-center rounded-lg',
            'bg-gradient-to-r from-dualite-primary-start to-dualite-primary-end',
            isMobile ? 'w-8 h-8' : 'h-8 gap-2 px-5',
          )}
        >
          <div className="i-ph:sparkle text-lg text-dualite-text-white" />
          {!isMobile && !chatStarted && (
            <span className="text-sm font-medium text-dualite-text-white">Prompt Enhanced</span>
          )}
          {!isMobile && chatStarted && <span className="text-sm font-medium text-dualite-text-white">Enhanced</span>}
        </div>

        {/* Redo button */}
        <button
          onClick={onRedo}
          className={classNames(
            'flex items-center justify-center rounded-lg transition-all',
            'bg-dualite-background-button hover:bg-dualite-background-button-hover',
            isMobile ? 'w-8 h-8' : 'h-8 gap-2 px-3',
          )}
          title="Redo enhancement"
        >
          <div className="i-ph:arrow-clockwise text-base text-dualite-text-white" />
          {!isMobile && <span className="text-sm font-medium text-dualite-text-white">Redo</span>}
        </button>
      </div>

      {/* Right side actions */}
      <div className="flex items-center gap-2">
        {/* Apply button */}
        <button
          onClick={onApply}
          className={classNames(
            'flex items-center justify-center h-8 rounded-lg transition-all',
            'bg-dualite-background-cta hover:bg-dualite-background-cta-hover',
            'px-5',
          )}
          title="Apply"
        >
          <span className="text-sm font-medium text-dualite-text-cta">Apply</span>
        </button>

        {/* Close/Reject button */}
        <button
          onClick={onReject}
          className={classNames(
            'flex items-center justify-center w-8 h-8 rounded-full transition-all',
            'border-2 border-dualite-background-cta',
          )}
          title="Reject"
        >
          <div className="i-ph:x text-lg text-dualite-text-primary" />
        </button>
      </div>
    </div>
  );
};
